<template>
  <section v-if="fields" :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <AdaptiveImage
      v-if="fields.backgroundImage"
      class="c-section-title__background-image"
      :field="fields.backgroundImage"
      :is-background="false"
      :show="true"
      :enable-scroll-detection="false"
      :show-image-redius="false"
    />

    <div class="c-section-title__content">
      <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
        <div class="c-section-title__inner">
          <div v-if="fields?.title.value" class="c-section-title__heading">
            <component :is="titleTag" class="c-section-title__title">
              <strong v-html="fields?.title.value"></strong>
            </component>
          </div>

          <div class="c-section-title__body">
            <span v-if="showDivider" class="c-section-title__divider" aria-hidden="true"></span>
            <RichText
              v-if="fields.description?.value"
              class="c-section-title__description"
              :field="fields.description"
            />
            <div v-if="buttons.length" class="c-section-title__actions">
              <SiteButton
                v-for="(button, index) in buttons"
                :key="button.id || index"
                v-bind="button"
              />
            </div>
          </div>
        </div>
      </ContentGrid>

      <div v-if="hasPlaceholderContent || nestedComponents.length" class="c-section-title__nested">
        <Placeholder v-if="hasPlaceholderContent" name="jss-section" :rendering="rendering" />
        <component
          :is="resolveDynamicComponent(componentData)"
          v-for="(componentData, componentIndex) in nestedComponents"
          v-else
          v-bind="getDynamicComponentProps(componentData)"
          :key="componentData?.uid ?? componentData?.id ?? `section-title-${componentIndex}`"
        />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Placeholder, RichText } from '@sitecore-jss/sitecore-jss-vue';
import type { ComponentRendering } from '@sitecore-jss/sitecore-jss-vue';
import type { SectionTitleProps } from '@/types/components/SectionTitle';
import type { ScItem } from '@/types/sitecore';
import type { SiteButtonFields } from '@/types/elements/site-button';
import AdaptiveImage from '@/elements/AdaptiveImage.vue';
import componentFactory from '@/temp/componentFactory';
import utils from '@/utils';

type DynamicComponentItem = ComponentRendering & {
  id?: string;
  template?: string;
  templateName?: string;
};

const props = defineProps<SectionTitleProps>();
const fields = computed(() => props.fields);
const rendering = computed(() => props.rendering);

const layout = computed(() => fields.value?.layout?.fields?.phrase?.value || 'split');
const isChecked = (value: unknown) =>
  value === true || value === 1 || value === '1' || value === 'true';
const titleTag = computed(() => (isChecked(fields.value?.isH1?.value) ? 'h1' : 'h2'));
const showDivider = computed(() => Boolean(fields.value?.showDivider?.value));
const hasHorizontalMargin = computed(() => Boolean(fields.value?.hasHorizontalMargin?.value));
const buttons = computed<ScItem<SiteButtonFields>[]>(() => {
  const buttonItems = utils.sc.getFieldObj(fields.value, 'buttons');
  return Array.isArray(buttonItems) ? buttonItems : [];
});
const nestedComponents = computed<DynamicComponentItem[]>(() => {
  const components = utils.sc.getFieldObj(fields.value, 'components');
  return Array.isArray(components)
    ? components.map((componentData) => ({
        ...componentData,
        componentName: getComponentName(componentData),
      }))
    : [];
});
const placeholderRenderings = computed<ComponentRendering[]>(() => {
  const placeholders = rendering.value?.placeholders as Record<string, ComponentRendering[]>;
  const renderings = placeholders?.['jss-section'];
  return Array.isArray(renderings) ? renderings : [];
});
const hasPlaceholderContent = computed(() => placeholderRenderings.value.length > 0);

const rootStyle = computed(() => ({
  ...(utils.overall?.baseComStyle?.(fields.value) || {}),
}));

const rootClass = computed(() => [
  utils.overall.baseComClass(props.fields, 'c-section-title'),
  `c-section-title--${layout.value}`,
  {
    'c-section-title--with-divider': showDivider.value,
    'c-section-title--with-actions': buttons.value.length > 0,
    'c-section-title--with-background': Boolean(fields.value?.backgroundImage),
    'c-section-title--with-horizontal-margin': hasHorizontalMargin.value,
  },
]);

function getComponentName(componentData?: DynamicComponentItem) {
  return `${
    componentData?.componentName ||
    componentData?.templateName ||
    componentData?.template ||
    'ImageTextCard'
  }`.replace(/\s+/g, '');
}

function resolveDynamicComponent(componentData?: DynamicComponentItem) {
  const componentName = getComponentName(componentData);
  return componentFactory(componentName) || componentName;
}

function getDynamicComponentProps(componentData: DynamicComponentItem) {
  return {
    fields: componentData?.fields ?? {},
    rendering: componentData,
  };
}
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-section-title {
  padding: 40px 0;
  &__background-image {
    display: block;
    width: 100%;
    height: auto;
  }

  &__background-image > div,
  &__background-image .e-background-image,
  &__background-image .e-background-image__img,
  &__background-image picture,
  &__background-image img,
  &__background-image .background-image {
    display: block;
    width: 100%;
    height: auto;
  }

  &__content {
    position: relative;
    z-index: 1;
  }

  &__inner {
    display: grid;
    gap: 24px;
    max-width: 1440px;
  }

  &__title {
    margin: 0;
    font-size: 32px;
    line-height: 1.18;
    font-weight: 600;

    p {
      margin: 0;
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  &__description {
    font-size: 16px;
  }

  &__divider {
    display: inline-block;
    width: 24px;
    height: 10px;
    margin: 0 0 14px;
    transform: skewX(-40deg);
    background: $color-growth-green;
    border-radius: 0 4px 0 4px;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    width: 100%;
    margin-top: 28px;
  }

  &__nested {
    margin-top: 28px;
  }

  &--with-background {
    display: grid;
    padding: 0;

    .c-section-title__background-image,
    .c-section-title__content {
      grid-area: 1 / 1;
    }

    .c-section-title__content {
      align-self: stretch;
      display: grid;
      align-content: center;
      padding: 40px 0;
    }
  }

  &--with-horizontal-margin {
    width: calc(100%);
    margin-right: auto;
    margin-left: auto;
    overflow: hidden;
    border-radius: 8px;

    .e-content-grid {
      padding-top: 0;
      padding-bottom: 0;
      margin: 0 !important;
    }
  }

  &--center,
  &--middle {
    text-align: center;

    .c-section-title__body {
      align-items: center;
    }

    .c-section-title__description {
      margin-right: auto;
      margin-left: auto;
    }
  }

  @include tablet {
    &--with-background {
      .c-section-title__content {
        padding: 40px 0;
      }
    }

    &--with-horizontal-margin {
      width: calc(100%);
      border-radius: 10px;

      .e-content-grid {
        padding-top: 0;
        padding-bottom: 0;
        margin: 0 !important;
      }
    }

    &__inner {
      gap: 32px;
    }

    &__title {
      font-size: 44px;
      line-height: 1.15;
    }

    &__description {
      font-size: 18px;
      line-height: 1.5;
    }

    &__actions {
      flex-direction: row;
      justify-content: center;
      width: auto;
    }

    &__nested {
      margin-top: 36px;
    }
  }

  @include tablet-landscape {
    padding: 60px 0;
    &--with-background {
      padding: 0;
    }

    &--with-horizontal-margin {
      width: calc(100% - 168px);
      max-width: 1440px;
      border-radius: 12px;

      .e-content-grid {
        padding-top: 0;
        padding-bottom: 0;
        margin: 0 !important;
      }
    }

    &__title {
      font-size: 44px;
    }

    &--split {
      .c-section-title__inner {
        grid-template-columns: minmax(0, 0.95fr) minmax(420px, 1.05fr);
        align-items: end;
        gap: 40px;
      }

      .c-section-title__description {
        padding-top: 8px;
      }
    }
  }
  &--with-horizontal-margin {
    padding-top: 0;
    padding-bottom: 0;

    .c-section-title__content,
    .e-content-grid {
      padding-top: 0;
      padding-bottom: 0;
      margin: 0 !important;
    }

    .e-content-grid__content {
      grid-column: 1 / -1;
    }

    .c-section-title__inner {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
      box-sizing: border-box;
      width: 100%;
      max-width: none;
      padding: 0;
    }
    .c-section-title__title {
      font-size: 32px;
      line-height: 1.18;
    }

    .c-section-title__description {
      font-size: 14px;
      line-height: 1.5;
    }
  }

  @media (min-width: $query-tablet-landscape) and (max-width: 1439px) {
    &--with-background:not(.c-section-title--with-horizontal-margin) {
      .c-section-title__content {
        padding: clamp(16px, 2vw, 28px) 0;
      }

      .c-section-title__inner {
        gap: clamp(16px, 2vw, 24px);
      }

      .c-section-title__divider {
        margin-bottom: clamp(8px, 1vw, 14px);
      }

      .c-section-title__actions {
        margin-top: clamp(14px, 1.5vw, 22px);
      }

      .c-section-title__nested {
        margin-top: clamp(16px, 2vw, 28px);
      }
    }
  }
}
</style>
